import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  Bell,
  AlertTriangle,
  AlertCircle,
  ChevronRight,
  CheckCircle2,
  GitPullRequest,
  MessageSquare,
  FileText,
  Link2
} from 'lucide-react';
import api from '../lib/api';
import { cn } from '../lib/utils';

const SEVERITY_CONFIG = {
  critical: {
    color: 'bg-red-500',
    textColor: 'text-red-500',
    bgLight: 'bg-red-500/10',
  },
  high: {
    color: 'bg-orange-500',
    textColor: 'text-orange-500',
    bgLight: 'bg-orange-500/10',
  },
  medium: {
    color: 'bg-yellow-500',
    textColor: 'text-yellow-500',
    bgLight: 'bg-yellow-500/10',
  },
  low: {
    color: 'bg-blue-500',
    textColor: 'text-blue-500',
    bgLight: 'bg-blue-500/10',
  },
};

const TYPE_ICONS = {
  stalled_pr: GitPullRequest,
  blocked_issue: Link2,
  high_discussion: MessageSquare,
  dependency_change: FileText,
};

export default function AlertIndicator({ onNewAlerts }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const loadAlerts = useCallback(async () => {
    try {
      const response = await api.get('/alerts?resolved=false&limit=10');
      const newAlerts = response.data || [];

      // Check for new critical/high alerts
      if (onNewAlerts && alerts.length > 0) {
        const existingIds = new Set(alerts.map(a => a.id));
        const newCriticalAlerts = newAlerts.filter(
          a => !existingIds.has(a.id) && (a.severity === 'critical' || a.severity === 'high')
        );
        if (newCriticalAlerts.length > 0) {
          onNewAlerts(newCriticalAlerts);
        }
      }

      setAlerts(newAlerts);
    } catch (error) {
      console.error('Failed to load alerts:', error);
    } finally {
      setLoading(false);
    }
  }, [alerts, onNewAlerts]);

  useEffect(() => {
    loadAlerts();
    // Poll every 60 seconds
    const interval = setInterval(loadAlerts, 60000);
    return () => clearInterval(interval);
  }, []);

  // Reload on open
  useEffect(() => {
    if (open) {
      loadAlerts();
    }
  }, [open]);

  const criticalCount = alerts.filter(a => a.severity === 'critical').length;
  const highCount = alerts.filter(a => a.severity === 'high').length;
  const totalCount = alerts.length;

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="alert-indicator"
        >
          <Bell className="h-5 w-5" />
          {totalCount > 0 && (
            <span
              className={cn(
                "absolute -top-1 -right-1 h-5 w-5 rounded-full flex items-center justify-center text-xs font-bold text-white",
                criticalCount > 0 ? 'bg-red-500' : highCount > 0 ? 'bg-orange-500' : 'bg-yellow-500'
              )}
            >
              {totalCount > 9 ? '9+' : totalCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="p-3 border-b">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold">Alerts</h4>
            {totalCount > 0 && (
              <div className="flex gap-1">
                {criticalCount > 0 && (
                  <Badge variant="destructive" className="text-xs">
                    {criticalCount} critical
                  </Badge>
                )}
                {highCount > 0 && (
                  <Badge variant="outline" className="text-xs text-orange-600 border-orange-600">
                    {highCount} high
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="max-h-80 overflow-auto">
          {loading ? (
            <div className="p-4 text-center text-muted-foreground">
              Loading...
            </div>
          ) : alerts.length === 0 ? (
            <div className="p-6 text-center">
              <CheckCircle2 className="h-8 w-8 mx-auto text-green-500 mb-2" />
              <p className="text-sm text-muted-foreground">No active alerts</p>
            </div>
          ) : (
            <div className="divide-y">
              {alerts.slice(0, 5).map((alert) => {
                const severityConfig = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.medium;
                const TypeIcon = TYPE_ICONS[alert.type] || AlertCircle;

                return (
                  <div
                    key={alert.id}
                    className="p-3 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex gap-3">
                      <div className={cn("p-1.5 rounded h-fit", severityConfig.bgLight)}>
                        {alert.severity === 'critical' ? (
                          <AlertTriangle className={cn("h-4 w-4", severityConfig.textColor)} />
                        ) : (
                          <TypeIcon className={cn("h-4 w-4", severityConfig.textColor)} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm line-clamp-2">{alert.message}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatTimeAgo(alert.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {alerts.length > 0 && (
          <div className="p-2 border-t">
            <Link to="/alerts" onClick={() => setOpen(false)}>
              <Button variant="ghost" className="w-full justify-between" size="sm">
                View all alerts
                <ChevronRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
